<template>
  <v-tour
    name="lenderIntroTour"
    :steps="steps"
    :callbacks="{
      onSkip: () => callbackOnEnd('skip'),
      onFinish: () => callbackOnEnd('finish'),
    }"
    :class="{ 'tour-active': isTouring }"
  >
    <template slot-scope="tour">
    <transition name="fade">
      <v-step
        v-for="(step, index) of tour.steps"
        :key="index"
        v-if="tour.currentStep === index"
        :step="step"
        :previous-step="tour.previousStep"
        :next-step="tour.nextStep"
        :stop="tour.stop"
        :finish="tour.finish"
        :skip="tour.skip"
        :is-first="tour.isFirst"
        :is-last="tour.isLast"
        :labels="tour.labels"
        :class="`step-${index + 1}`"
      >
        <div slot="actions" class="v-step__actions">
          <div class="progress-dots">
            <div
              v-for="index in 7"
              :key="index"
              class="dot"
              :class="{ active: tour.currentStep === index - 1 }"
            ></div>
          </div>
          <div class="v-step__buttons">
            <button
              v-if="!tour.isLast"
              @click="tour.skip" class="default-btn small slim text skip-button">
              Skip
            </button>

            <button
              v-if="!tour.isFirst"
              class="default-btn small slim text back-button"
              @click="tour.previousStep">
              Back
            </button>

            <button
              v-if="!tour.isLast"
              class="default-btn small slim text"
              @click="tour.nextStep">
              Next
              <v-icon right>
                mdi-arrow-right
              </v-icon>
            </button>

            <button
              v-else
              class="default-btn small slim text"
              @click="tour.finish">
              Finish
            </button>
          </div>
        </div>
      </v-step>
    </transition>
    </template>
  </v-tour>
</template>

<script>
import { mapGetters, mapState } from 'vuex'

export default {
  name: 'Tour',

  data () {
    return {
      user: null,
      isTouring: false,
      steps: [
        {
          content: 'Your dashboard gives you an overview of new leads by month, potential loan funding amount, new reviews, and most searched financing type.',
          header: {
            title: 'Welcome to Lender Dashboard',
          },
          target: '.nav.OrganizationDashboard',
          params: {
            placement: 'right-start',
            modifiers: {
              preventOverflow: { enabled: false },
              hide: { enabled: false },
              offset: {
                enabled: true,
                fn (data) {
                  data.offsets.popper.top -= 20
                  data.offsets.popper.left += 10
                  return data
                },
              },
            },
          },
          before: async type => {
            let waitTime = 1500
            try {
              await this.$router.replace({
                name: 'OrganizationDashboard',
                params: { organizationId: this.user.organizationId },
              })
            } catch (error) {
              waitTime = 500
            }

            if (type === 'start') {
              await (new Promise(resolve => {
                setTimeout(resolve, waitTime)
              }))
            }
          },
        },
        {
          content: 'The Leads tab shows a more detailed view of leads including matching criteria, loan request and contact information.',
          target: '.nav.OrganizationLeads',
          params: {
            placement: 'right-start',
            modifiers: {
              preventOverflow: { enabled: false },
              hide: { enabled: false },
              offset: {
                enabled: true,
                fn (data) {
                  data.offsets.popper.top -= 20
                  data.offsets.popper.left += 10
                  return data
                },
              },
            },
          },
          before: async type => {
            try {
              await this.$router.replace({
                name: 'OrganizationLeads',
                params: { organizationId: this.user.organizationId },
              })
            } catch (error) {}
          },
        },
        {
          content: 'Update the status of leads, marking a lead as funded will send an email to the client to review the lender. More reviews help visibility in the match process.',
          target: 'thead th.column-status',
          params: {
            placement: 'bottom-start',
            enableScrolling: false,
            modifiers: {
              preventOverflow: { enabled: false },
              hide: { enabled: false },
              flip: { enabled: false },
            },
          },
          before: async type => {
            try {
              await this.$router.replace({
                name: 'OrganizationLeads',
                params: { organizationId: this.user.organizationId },
              })
            } catch (error) {}
          },
        },
        {
          content: 'You can edit your match criteria in the Loan Product Criteria tab.',
          target: '.nav.OrganizationProducts',
          params: {
            placement: 'right-start',
            modifiers: {
              preventOverflow: { enabled: false },
              hide: { enabled: false },
              offset: {
                enabled: true,
                fn (data) {
                  data.offsets.popper.top -= 20
                  data.offsets.popper.left += 10
                  return data
                },
              },
            },
          },
          before: async type => {
            try {
              await this.$router.replace({
                name: 'OrganizationProducts',
                params: { organizationId: this.user.organizationId },
              })
            } catch (error) {}
          },
        },
        {
          content: 'Click Publish/Update to post changes to your loan product criteria to the matching algorithm. Here you can also unpublish live products that you no longer wish to show in the match.',
          target: 'button.publish-button',
          params: {
            placement: 'bottom-end',
            enableScrolling: false,
            modifiers: {
              preventOverflow: { enabled: false },
              hide: { enabled: false },
              offset: {
                enabled: true,
                fn (data) {
                  data.offsets.popper.top += 15
                  return data
                },
              },
            },
          },
          before: async type => {
            try {
              await this.$router.replace({
                name: 'OrganizationProducts',
                params: { organizationId: this.user.organizationId },
              })
            } catch (error) {}
          },
        },
        {
          content: 'You can view and respond to reviews in the Reviews Tab.',
          target: '.nav.OrganizationReviews',
          params: {
            placement: 'right-start',
            modifiers: {
              preventOverflow: { enabled: false },
              hide: { enabled: false },
              offset: {
                enabled: true,
                fn (data) {
                  data.offsets.popper.top -= 20
                  data.offsets.popper.left += 10
                  return data
                },
              },
            },
          },
          before: async type => {
            try {
              await this.$router.replace({
                name: 'OrganizationReviews',
                params: { organizationId: this.user.organizationId },
              })
            } catch (error) {}
          },
        },
        {
          content: 'Thank you for reviewing the Lender Dashboard. You can access this tutorial anytime by pressing help',
          target: 'button.begin-lender-tour',
          params: {
            placement: 'bottom-end',
            modifiers: {
              preventOverflow: { enabled: false },
              hide: { enabled: false },
              offset: {
                enabled: true,
                fn (data) {
                  data.offsets.popper.left += 20
                  return data
                },
              },
            },
          },
          before: async type => {
            try {
              await this.$router.replace({
                name: 'OrganizationDashboard',
                params: { organizationId: this.user.organizationId },
              })
            } catch (error) {}
          },
        },
      ],
      callbacks: {},
    }
  },

  computed: {
    ...mapState('feathers', {
      client: 'client',
    }),

    ...mapGetters('feathers', ['apiUsers']),
  },

  created () {
    this.client.on('login', ({ user }) => {
      this.user = user
      this.checkIfLenderNeedsTour(user)
    })

    this.client.on('logout', () => {
      this.user = null
      this.isTouring = false
      this.$tours.lenderIntroTour.stop()
    })

    this.client.on('beginTour:lenderIntroTour', () => {
      this.beginLenderTour()
    })
  },

  methods: {
    checkIfLenderNeedsTour (user) {
      if (user.role === 'lender' && !user.tourRanLenderIntro) {
        this.beginLenderTour()
      }
    },

    beginLenderTour () {
      this.isTouring = true
      this.$tours.lenderIntroTour.start()
    },

    callbackOnEnd (state = 'finish') {
      this.isTouring = false
      if (!this.user || this.user.tourRanLenderIntro) return

      this.user.tourRanLenderIntro = true
      this.apiUsers.patch(this.user.id, {
        tourRanLenderIntro: true,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.v-tour:not(.tour-active) {
  display: none;
}

.v-tour.tour-active {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;

  background-color: rgba(0, 0, 0, 0.1);
  z-index: 1000;
}

.v-tour .v-step {
  width: 270px;
  padding: 10px 10px 5px;

  display: flex;
  flex-direction: column;
  justify-content: space-between;

  background-color: white;
  border-radius: 5px;
  box-shadow: $primaryShadow;
  color: $textDarkMuted;
  filter: none;

  ::v-deep .v-step__header {
    margin: -10px -10px 0;
    padding: 10px 5px;

    background-color: white;
    font-family: "SansationRegular", sans-serif;
    font-size: 16px;
  }

  ::v-deep .v-step__content {
    padding: 10px;

    font-family: "Avenir Book", sans-serif;
    font-size: 16px;
    line-height: 18px;
    text-align: left;
  }

  .v-step__buttons {
    margin-top: 5px;

    display: flex;
    justify-content: flex-end;

    .default-btn {
      margin: 0;
      color: $textDarkMuted !important;
      position: relative;

      &.skip-button {
        margin-right: auto;
      }

      &.back-button:after {
        content: '';
        position: absolute;
        top: 7px;
        bottom: 7px;
        right: -4px;
        width: 2px;
        background-color: #E4E4E4;
      }
    }
  }

  .progress-dots {
    display: flex;
    justify-content: center;

    .dot {
      height: 10px;
      width: 10px;
      margin: 0 4px;

      background-color: #E4E4E4;
      border-radius: 50%;

      &.active {
        background-color: $textDarkMuted;
      }
    }
  }
}

.v-step[x-placement^=right]::v-deep .v-step__arrow {
  border-width: 15px 15px 15px 0;
  left: -15px;

  &:before {
    top: -16px;
    left: -1px;
    border-width: 16px 16px 16px 0;
    border-left-color: transparent;
    border-top-color: transparent;
    border-bottom-color: transparent;
  }
}

.v-step[x-placement^=bottom]::v-deep .v-step__arrow {
  border-width: 0 15px 15px 15px;
  top: -15px;

  &:before {
    left: -16px;
    top: -1px;
    border-width: 0 16px 16px 16px;
    border-top-color: transparent;
    border-left-color: transparent;
    border-right-color: transparent;
  }
}

.v-step::v-deep .v-step__arrow {
  border-color: white;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 0;
    height: 0;

    border: 0 solid #00000029;
    z-index: -1;
  }
}

.v-step.step-1,
.v-step.step-2,
.v-step.step-4,
.v-step.step-6 {
  ::v-deep .v-step__arrow {
    top: 18px;
  }
}

.v-step.step-3 {
  ::v-deep .v-step__arrow {
    left: 18px;
  }
}

.v-step.step-5,
.v-step.step-7 {
  ::v-deep .v-step__arrow {
    left: 200px;
  }
}
</style>
